/**
 * A tappable card slot: shows a PlayingCard when filled, or a dashed "+"
 * placeholder when empty. Tapping opens a CardPicker; the parent owns the
 * picker open/close + which cards are disabled (so dedup logic lives in one
 * place — the builder).
 */

import { useState } from "react";

import { PlayingCard } from "@/features/chat/components/PlayingCard";
import { Plus, X } from "@/shared/components/icons";
import { CardPicker } from "./CardPicker";

const SLOT_DIMS = {
  sm: "w-11 h-[62px]",
  md: "w-[54px] h-[76px]",
  lg: "w-[72px] h-[100px]",
} as const;

export function CardSlot({
  token,
  size = "md",
  pickerTitle,
  disabled,
  onPick,
  onClear,
  ariaLabel = "Card slot",
}: {
  /** Current card, or "" / null when empty. */
  token: string | null;
  size?: "sm" | "md" | "lg";
  pickerTitle?: string;
  /** Cards that can't be chosen for this slot (used elsewhere). */
  disabled: Set<string>;
  onPick: (token: string) => void;
  /** When provided, a small clear (×) affordance shows on a filled slot. */
  onClear?: () => void;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const filled = !!token;

  return (
    <>
      <div className="relative inline-flex">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={ariaLabel}
          className={[
            "flex items-center justify-center rounded-md transition",
            filled
              ? "active:scale-95"
              : `${SLOT_DIMS[size]} border-2 border-dashed border-border text-text-muted hover:border-accent hover:text-accent`,
          ].join(" ")}
        >
          {filled ? (
            <PlayingCard token={token!} size={size} />
          ) : (
            <Plus size={20} strokeWidth={2} />
          )}
        </button>
        {filled && onClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear card"
            className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-surface text-text-muted shadow hover:text-text"
          >
            <X size={12} strokeWidth={2.25} />
          </button>
        )}
      </div>

      {open && (
        <CardPicker
          title={pickerTitle}
          disabled={disabled}
          onPick={(t) => {
            onPick(t);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
